import React, { useState } from 'react';
import { Edit2, Ban, CheckCircle } from 'lucide-react';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { useCompany } from '../context/CompanyContext';
import { sanitizeForFirestore } from '../utils/firestoreUtils';
import { isAdminLike } from '../utils/roles';
import RoleBadge from './RoleBadge';
import { Button } from './ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { toast } from 'sonner';

const UserRow = ({ user, currentUserId, isAdmin, onUpdate }) => {
  const { companyId } = useCompany();
  const isAdminLikeUser = isAdminLike(user.role);
  const [editing, setEditing] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [selectedRole, setSelectedRole] = useState(user.role);

  const isCurrentUser = user.id === currentUserId;

  const handleRoleChange = async () => {
    if (selectedRole === user.role) {
      setEditing(false);
      return;
    }

    setUpdating(true);

    try {
      if (!companyId) throw new Error('Missing companyId');
      // Role is stored in company membership doc
      const membershipRef = doc(db, 'companies', companyId, 'users', user.id);
      await updateDoc(
        membershipRef,
        sanitizeForFirestore({
          role: selectedRole,
          updatedAt: serverTimestamp(),
        })
      );

      toast.success('User role updated successfully');
      if (onUpdate) onUpdate();
      setEditing(false);
    } catch (error) {
      console.error('Error updating user role:', error);
      toast.error('Failed to update user role');
      setSelectedRole(user.role); // Revert
    } finally {
      setUpdating(false);
    }
  };

  const handleToggleStatus = async () => {
    const newStatus = user.status === 'active' ? 'inactive' : 'active';

    setUpdating(true);

    try {
      // Status is a profile field; keep it in /users/{uid}
      const userDocRef = doc(db, 'users', user.id);
      await updateDoc(
        userDocRef,
        sanitizeForFirestore({
          status: newStatus,
          updatedAt: serverTimestamp(),
        })
      );

      toast.success(`User ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully`);
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error updating user status:', error);
      toast.error('Failed to update user status');
    } finally {
      setUpdating(false);
    }
  };

  return (
    <tr className="border-b hover:bg-gray-50">
      <td className="py-3 px-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden">
            {user.photoURL ? (
              <img
                src={user.photoURL}
                alt={user.displayName || 'User'}
                className="h-full w-full object-cover"
              />
            ) : (
              <span className="text-sm font-medium text-gray-600">
                {(user.displayName || user.email || 'U').charAt(0).toUpperCase()}
              </span>
            )}
          </div>
          <div>
            <div className="font-medium text-gray-900">
              {user.displayName || 'Unnamed'}
              {isCurrentUser && (
                <span className="ml-2 text-xs text-gray-500">(You)</span>
              )}
            </div>
            {user.phoneNumber && (
              <div className="text-xs text-gray-500">{user.phoneNumber}</div>
            )}
          </div>
        </div>
      </td>

      <td className="py-3 px-4 text-sm text-gray-700">{user.email}</td>

      <td className="py-3 px-4">
        {editing && isAdmin && !isCurrentUser && !isAdminLikeUser ? (
          <div className="flex items-center gap-2">
            <Select value={selectedRole} onValueChange={setSelectedRole}>
              <SelectTrigger className="w-36 h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="sales">Sales</SelectItem>
                <SelectItem value="technician">Technician</SelectItem>
                <SelectItem value="marketing">Marketing</SelectItem>
              </SelectContent>
            </Select>
            <Button
              size="sm"
              onClick={handleRoleChange}
              disabled={updating}
              className="h-7 text-xs"
            >
              {updating ? 'Saving...' : 'Save'}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setEditing(false);
                setSelectedRole(user.role);
              }}
              disabled={updating}
              className="h-7 text-xs"
            >
              Cancel
            </Button>
          </div>
        ) : (
          <RoleBadge role={user.role} size="sm" />
        )}
      </td>

      <td className="py-3 px-4">
        <span
          className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
            user.status === 'active'
              ? 'bg-green-100 text-green-700'
              : 'bg-gray-100 text-gray-700'
          }`}
        >
          {user.status === 'active' ? (
            <CheckCircle className="h-3 w-3" />
          ) : (
            <Ban className="h-3 w-3" />
          )}
          {user.status === 'active' ? 'Active' : 'Inactive'}
        </span>
      </td>

      <td className="py-3 px-4">
        {isAdmin && !isCurrentUser && !isAdminLikeUser && (
          <div className="flex items-center gap-2">
            {!editing && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setEditing(true)}
                disabled={updating}
                className="h-8 text-xs"
              >
                <Edit2 className="h-3 w-3 mr-1" />
                Edit Role
              </Button>
            )}
            <Button
              size="sm"
              variant="outline"
              onClick={handleToggleStatus}
              disabled={updating || editing}
              className="h-8 text-xs"
            >
              {user.status === 'active' ? 'Deactivate' : 'Activate'}
            </Button>
          </div>
        )}
        {!isAdmin && (
          <span className="text-xs text-gray-400">View only</span>
        )}
      </td>
    </tr>
  );
};

export default UserRow;
